import { z } from 'zod';
// Audit action vocabulary — single source of truth for `audit_logs.action`.
//
// Per ADR-020: `action` is `varchar(32)` with vocabulary in code (no DB CHECK
// constraint). The vocabulary lives here so it can be type-checked at compile
// time on both backend and frontend, and refactored without a schema migration.
//
// Scope-negative discipline (ADR-020): `audit_logs` records entity changes
// plus a small finite set of system events. Operational / per-step processing
// detail belongs in the shared `import_jobs` / `import_job_rows` pair (ADR-041,
// revising ADR-020 §Two-tier to one shared pair) linked to the Tier-1 audit row
// by shared `correlation_id`.
//
// Add new actions alongside the code that emits them. Each name is a snake_case verb
// or verb_object, ≤ 32 chars to fit `varchar(32)`.
export const AUDIT_ACTIONS = [
    // Generic entity-change verbs
    'create',
    'update',
    'delete',
    'clone',
    'reactivate',
    'decommission',
    'activate',
    'disable',
    // Project lifecycle (sysadmin platform surface; reactivate is shared above).
    'suspend',
    'terminate',
    'reorder',
    'follow',
    'unfollow',
    'associate',
    'remove_from_project',
    'handover', // a person's responsibility chains given to successors, or left (ADR-081 §5)
    // Assignment / permission
    'assign',
    'revoke',
    // Solution-config timeline verbs + suggestion acts (ADR-056 §2/§7 — the
    // human resolutions; observation WRITES are deliberately not audited)
    'config_changed',
    'config_corrected',
    'config_start_date_corrected',
    'config_period_deleted',
    'suggestion_ignored',
    'suggestion_restored',
    // Designation-SCD timeline verbs (ADR-060 §3 — same grammar as the
    // solution-config verbs; the first period audits as a plain `create`)
    'designation_changed',
    'designation_corrected',
    'designation_start_corrected',
    'designation_period_deleted',
    // Site lifecycle DAYS repaired in place (the correction door + the lifecycle
    // import's already-at-target row) — a repair, never a transition; the
    // config_start_date_corrected family.
    'lifecycle_dates_corrected',
    // Auth + session events
    'login',
    'logout',
    'login_fail',
    'mfa_fail',
    'account_locked',
    'password_reset',
    'password_changed',
    'mfa_enrolled',
    'mfa_self_enrolled',
    'mfa_self_disabled',
    'mfa_authenticator_reset',
    'toggle_mfa',
    'toggle_active',
    // Notifications (foundation: email only; in-app + Telegram at APP-065)
    'email_sent',
    // External-identity onboarding (the chat-account link — ADR-004). The credential
    // is NEVER in the payload; these record the ACT and its target only.
    'external_identity_invited',
    'external_identity_invite_revoked',
    'external_identity_linked',
    'external_identity_disconnected',
    'external_identity_test_message',
    // Application credentials (api_clients — ADR-004). Create/edit ride the
    // generic `create`/`update`; the credential lifecycle gets its own verbs (the
    // generic `revoke` is a permission-grant `link`, not a credential state change).
    // Payloads carry the overlap window / hint at most — never a token or hash.
    'api_client_rotated',
    'api_client_revoked',
    // Personal access tokens (user_api_keys — ADR-004). Create/rename ride
    // the generic `create`/`update`; revocation gets its own verb (the generic
    // `revoke` is a permission-grant `link`, not a credential state change — the
    // api_client_revoked precedent). Rows land in the token's PINNED project's trail.
    // Payloads carry label · project · permission list · expiry — never token/hash.
    'pat_revoked',
    // Asset register verbs (ADR-063 — the audit-side mirror of the locked
    // asset_events vocabulary; `create` + `edit` ride the generic create/update,
    // and `reactivate` reuses the generic lifecycle word above. The event row is
    // the domain ledger; these rows are the forensic envelope — the deliberate
    // two-tier overlap). `event_date_corrected` is the correction door's diff
    // (the config_start_date_corrected precedent — a repair, never a new event).
    'move',
    'replace',
    'adjust',
    'put_on_hold',
    'reinstate',
    'retire',
    'declare_lost',
    'recover',
    'write_off',
    'dispose',
    'transfer_ownership',
    'event_date_corrected',
    // The audited per-field credential reveal (ADR-063 §access facet): one row
    // per look — params carry {kind, field} markers, NEVER the value.
    'credential_revealed',
    // An explicit re-submission of a secret whose plaintext EQUALS the stored one:
    // the sealed-compare keeps the ciphertext (no change), so this row is what
    // stops that being a silent confirm-a-value-you-can't-read oracle for an
    // asset:write holder without reveal rights. Params {kind, field}, never the value.
    'credential_confirmed',
    // Products catalog (ADR-065): a release's availability flips — the import
    // maps the record's `withdrawn` flag onto canonical is_disabled; the act
    // audits platform-side + one correlated row per project with assets on the
    // model (the merge's fan-out pattern).
    'withdrawn',
    'reoffered',
    // Products catalog (ADR-065): the sysadmin merge verb — one correlated row
    // per affected project + a platform-scoped row for the act.
    'merge',
    // Bulk-import (Tier-1 summary rows; per-record detail lives in the shared
    // import_jobs / import_job_rows pair per ADR-041, revising ADR-020 §Two-tier)
    'import_started',
    'import_completed',
    'import_failed',
    // Exports
    'export',
    // Files (ADR-068 §5 — reads don't audit except reveals · exports · DOWNLOADS ·
    // previews of sensitive classes). Markers only (file id · class · folder), never
    // the content; the credential class rides `credential_revealed` instead. The
    // writes carry their own verbs so the trail reads as a sentence: classified
    // (one row per decision; a bulk answer = one row with the count) · moved (the
    // link row re-pointed) · replaced (a new version) · restored (from the bin) ·
    // purged (the sweeper's destruction record — the row is gone, this row is the
    // file's only memory; one per version family, inside the purge transaction);
    // commit = create, rename/caption = update, delete = delete; a note never audits.
    'file_downloaded',
    'file_previewed',
    'file_classified',
    'file_moved',
    'file_replaced',
    'file_restored',
    'file_purged',
    // Tickets (the capture skeleton). The ceremony verbs read as
    // sentences: checked in (a visit opened, the fix's verdict in the snapshot) ·
    // re-checked (the same-site refresh — the OLD verdict lives in previous_value:
    // the honest early-checker's breadcrumb) · closed (reason: checked_out ·
    // superseded · expired). The capture itself audits as its FILE's commit row.
    'ticket_created',
    'visit_checked_in',
    'visit_rechecked',
    'visit_closed',
    // The capture lifecycle (ADR-073): the audit takes little, the journal takes
    // the volume — one row per act by a PERSON (closed · reopened; a batch close
    // is one act = one row with the count + ids), one row per RUN by the platform
    // (the grace sweep filing n tickets · a re-judge that changed n tickets),
    // never a row per stage move caused by another act (the review answer's row
    // is `file_classified`; the visit's end is `visit_closed`).
    'ticket_closed',
    'ticket_reopened',
    'tickets_closed',
    'tickets_graced',
    'tickets_rejudged',
];
export const AuditActionSchema = z.enum(AUDIT_ACTIONS);
// ── Record kinds (ADR-058) ──────────────────────────────────────────────────
// Every audit row has a `kind` — the shape discriminator the reader switches on
// to render the row from itself alone (no re-derivation of what changed, no live
// entity join). The six kinds are the only structural shapes a diary entry takes:
//   created      — after-snapshot (a new thing)                     → new_value = snapshot
//   edited       — before→after diff of the changed fields only     → previous_value/new_value = changed-only
//   state_change — a verb-carried transition (to-state)             → new_value = { to, ...params }
//   link         — many-valued / attributed relationship change     → new_value = { added[], removed[] }
//   event        — a typed descriptor; no before/after              → new_value = descriptor
//   deleted      — before-snapshot (what was removed)               → previous_value = snapshot
// Additive: a new kind is a value here + a write helper + one reader branch; the
// reader keeps a safe fallback for an unknown kind (append-only rows live forever).
export const RECORD_KINDS = ['created', 'edited', 'state_change', 'link', 'event', 'deleted'];
export const RecordKindSchema = z.enum(RECORD_KINDS);
// The action→kind map is a TOTAL function (every action declares its kind — the
// `satisfies Record<AuditAction, ...>` makes a missing entry a compile error, and
// audit/actions.test.ts asserts totality at runtime). `kind` is derived from this
// map and STORED at write time (core/audit), so a new action never needs a schema
// migration — only a row here — preserving ADR-020's "vocabulary in code".
export const AUDIT_ACTION_KIND = {
    // created — a new thing (snapshot)
    create: 'created',
    clone: 'created',
    file_replaced: 'created',
    ticket_created: 'created',
    visit_checked_in: 'created',
    // edited — scalar/attribute field diff (changed-only)
    update: 'edited',
    config_corrected: 'edited',
    config_start_date_corrected: 'edited',
    designation_corrected: 'edited',
    designation_start_corrected: 'edited',
    lifecycle_dates_corrected: 'edited',
    event_date_corrected: 'edited',
    file_moved: 'edited',
    visit_rechecked: 'edited',
    // state_change — verb-carried lifecycle transition
    activate: 'state_change',
    reactivate: 'state_change',
    decommission: 'state_change',
    disable: 'state_change',
    suspend: 'state_change',
    terminate: 'state_change',
    account_locked: 'state_change',
    move: 'state_change',
    replace: 'state_change',
    put_on_hold: 'state_change',
    reinstate: 'state_change',
    retire: 'state_change',
    declare_lost: 'state_change',
    recover: 'state_change',
    write_off: 'state_change',
    dispose: 'state_change',
    mfa_self_enrolled: 'state_change',
    mfa_self_disabled: 'state_change',
    toggle_mfa: 'state_change',
    toggle_active: 'state_change',
    api_client_rotated: 'state_change',
    api_client_revoked: 'state_change',
    pat_revoked: 'state_change',
    file_restored: 'state_change',
    visit_closed: 'state_change',
    ticket_closed: 'state_change',
    ticket_reopened: 'state_change',
    // link — a relationship established / removed between the subject and other entities
    associate: 'link',
    remove_from_project: 'link',
    handover: 'link',
    assign: 'link',
    revoke: 'link',
    follow: 'link',
    unfollow: 'link',
    transfer_ownership: 'link',
    // event — a typed descriptor, no before/after
    external_identity_invited: 'event',
    external_identity_invite_revoked: 'event',
    external_identity_linked: 'event',
    external_identity_disconnected: 'event',
    external_identity_test_message: 'event',
    reorder: 'event',
    config_changed: 'event',
    designation_changed: 'event',
    suggestion_ignored: 'event',
    suggestion_restored: 'event',
    login: 'event',
    logout: 'event',
    login_fail: 'event',
    mfa_fail: 'event',
    password_reset: 'event',
    password_changed: 'event',
    mfa_enrolled: 'event',
    mfa_authenticator_reset: 'event',
    email_sent: 'event',
    adjust: 'event',
    credential_revealed: 'event',
    credential_confirmed: 'event',
    merge: 'event',
    withdrawn: 'event',
    reoffered: 'event',
    import_started: 'event',
    import_completed: 'event',
    import_failed: 'event',
    export: 'event',
    file_downloaded: 'event',
    file_previewed: 'event',
    file_classified: 'event',
    tickets_closed: 'event',
    tickets_graced: 'event',
    tickets_rejudged: 'event',
    // deleted — before-snapshot of what was removed
    delete: 'deleted',
    config_period_deleted: 'deleted',
    designation_period_deleted: 'deleted',
    file_purged: 'deleted',
};
// Kind of an action — the single lookup the reader uses to render a row.
export function kindOf(action) {
    return AUDIT_ACTION_KIND[action];
}
// ── the families — the thing a verb applies to (the audit page's Action filter) ──
// The vocabulary is filtered as a WHOLE (never from the lines on screen), so a
// flat list of every verb would be 87 sentence pieces; grouped by the thing
// they apply to, a person finds "checked in" under Field work and "downloaded"
// under Files. `any` gathers the verbs every kind of thing takes. A TOTAL map
// (the `satisfies` makes a missing verb a compile error). The entity side is
// `AUDIT_ENTITY_FAMILY` (entities.ts) — the same family words.
export const AUDIT_FAMILIES = ['any', 'people', 'sites', 'contract', 'assets', 'files', 'field_work', 'project'];
export const AUDIT_ACTION_FAMILY = {
    // any — the verbs every kind of thing takes
    create: 'any',
    update: 'any',
    delete: 'any',
    clone: 'any',
    reactivate: 'any',
    activate: 'any',
    disable: 'any',
    suspend: 'any',
    reorder: 'any',
    follow: 'any',
    unfollow: 'any',
    export: 'any',
    import_started: 'any',
    import_completed: 'any',
    import_failed: 'any',
    associate: 'any', // a person to a project, an owner to a unit
    // people — accounts, sessions, access
    remove_from_project: 'people',
    handover: 'people',
    assign: 'people',
    revoke: 'people',
    login: 'people',
    logout: 'people',
    login_fail: 'people',
    mfa_fail: 'people',
    account_locked: 'people',
    password_reset: 'people',
    password_changed: 'people',
    mfa_enrolled: 'people',
    mfa_self_enrolled: 'people',
    mfa_self_disabled: 'people',
    mfa_authenticator_reset: 'people',
    toggle_mfa: 'people',
    toggle_active: 'people',
    external_identity_invited: 'people',
    external_identity_invite_revoked: 'people',
    external_identity_linked: 'people',
    external_identity_disconnected: 'people',
    external_identity_test_message: 'people',
    api_client_rotated: 'people',
    api_client_revoked: 'people',
    pat_revoked: 'people',
    // sites — the record, its lifecycle, its configuration timelines
    decommission: 'sites',
    config_changed: 'sites',
    config_corrected: 'sites',
    config_start_date_corrected: 'sites',
    config_period_deleted: 'sites',
    suggestion_ignored: 'sites',
    suggestion_restored: 'sites',
    designation_changed: 'sites',
    designation_corrected: 'sites',
    designation_start_corrected: 'sites',
    designation_period_deleted: 'sites',
    lifecycle_dates_corrected: 'sites',
    // assets — the register's verbs, the catalog's, the consumables'
    move: 'assets',
    replace: 'assets',
    adjust: 'assets',
    put_on_hold: 'assets',
    reinstate: 'assets',
    retire: 'assets',
    declare_lost: 'assets',
    recover: 'assets',
    write_off: 'assets',
    dispose: 'assets',
    transfer_ownership: 'assets',
    event_date_corrected: 'assets',
    credential_revealed: 'assets',
    credential_confirmed: 'assets',
    merge: 'assets', // a duplicate product into its canonical
    withdrawn: 'assets',
    reoffered: 'assets',
    // files
    file_downloaded: 'files',
    file_previewed: 'files',
    file_classified: 'files',
    file_moved: 'files',
    file_replaced: 'files',
    file_restored: 'files',
    file_purged: 'files',
    // field work — tickets and visits
    ticket_created: 'field_work',
    visit_checked_in: 'field_work',
    visit_rechecked: 'field_work',
    visit_closed: 'field_work',
    ticket_closed: 'field_work',
    ticket_reopened: 'field_work',
    tickets_closed: 'field_work',
    tickets_graced: 'field_work',
    tickets_rejudged: 'field_work',
    // project — what the project itself does
    terminate: 'project',
    email_sent: 'project',
};
export function familyOf(action) {
    return AUDIT_ACTION_FAMILY[action];
}
// ── the six words — the audit page's Action filter, a reader's coarse axis ────
// What people come to a trail to ask is rarely a verb: "what was deleted this
// week", "who signed in", "what did the platform do on its own". The six words
// are a TOTAL map over the vocabulary (the `satisfies` makes a missing verb a
// compile error; vocabulary.test.ts asserts it at runtime), read by the audit
// read to expand one word into its verbs (`action IN (…)` on the action index)
// — no stored column, no migration when a verb joins: a row here and the word
// knows it. The placement rule: a thing BORN is created; a thing's fields,
// state, place, rights or links CHANGED is changed (a right given or taken is a
// change to the person — there is no separate "access" word); a thing REMOVED
// is deleted; something LOOKED AT that leaves a trace (a download, a preview,
// an export, a secret revealed) is viewed; the account's own door — in, out,
// failed, locked, a password or an authenticator — is sign_in; what the
// platform did by itself (an import run, a sweep, a sent mail) is system.
export const AUDIT_ACTION_TYPES = ['created', 'changed', 'deleted', 'viewed', 'sign_in', 'system'];
export const AuditActionTypeSchema = z.enum(AUDIT_ACTION_TYPES);
export const AUDIT_ACTION_TYPE = {
    // created — a thing born
    create: 'created',
    clone: 'created',
    file_replaced: 'created',
    ticket_created: 'created',
    visit_checked_in: 'created',
    // changed — fields, a state, a place, a right, a link
    update: 'changed',
    config_corrected: 'changed',
    config_start_date_corrected: 'changed',
    designation_corrected: 'changed',
    designation_start_corrected: 'changed',
    lifecycle_dates_corrected: 'changed',
    event_date_corrected: 'changed',
    file_moved: 'changed',
    visit_rechecked: 'changed',
    activate: 'changed',
    reactivate: 'changed',
    decommission: 'changed',
    disable: 'changed',
    suspend: 'changed',
    terminate: 'changed',
    move: 'changed',
    replace: 'changed',
    put_on_hold: 'changed',
    reinstate: 'changed',
    retire: 'changed',
    declare_lost: 'changed',
    recover: 'changed',
    write_off: 'changed',
    dispose: 'changed',
    file_restored: 'changed',
    visit_closed: 'changed',
    ticket_closed: 'changed',
    ticket_reopened: 'changed',
    toggle_mfa: 'changed',
    toggle_active: 'changed',
    api_client_rotated: 'changed',
    api_client_revoked: 'changed',
    pat_revoked: 'changed',
    associate: 'changed',
    remove_from_project: 'changed',
    handover: 'changed',
    assign: 'changed',
    revoke: 'changed',
    follow: 'changed',
    unfollow: 'changed',
    transfer_ownership: 'changed',
    reorder: 'changed',
    config_changed: 'changed',
    designation_changed: 'changed',
    suggestion_ignored: 'changed',
    suggestion_restored: 'changed',
    adjust: 'changed',
    merge: 'changed',
    withdrawn: 'changed',
    reoffered: 'changed',
    file_classified: 'changed',
    external_identity_invited: 'changed',
    external_identity_invite_revoked: 'changed',
    external_identity_linked: 'changed',
    external_identity_disconnected: 'changed',
    external_identity_test_message: 'changed',
    // deleted — a thing removed
    delete: 'deleted',
    config_period_deleted: 'deleted',
    designation_period_deleted: 'deleted',
    file_purged: 'deleted',
    // viewed — looked at, and the look leaves a trace
    file_downloaded: 'viewed',
    file_previewed: 'viewed',
    export: 'viewed',
    credential_revealed: 'viewed',
    credential_confirmed: 'viewed',
    // sign_in — the account's own door
    login: 'sign_in',
    logout: 'sign_in',
    login_fail: 'sign_in',
    mfa_fail: 'sign_in',
    account_locked: 'sign_in',
    password_reset: 'sign_in',
    password_changed: 'sign_in',
    mfa_enrolled: 'sign_in',
    mfa_self_enrolled: 'sign_in',
    mfa_self_disabled: 'sign_in',
    mfa_authenticator_reset: 'sign_in',
    // system — the platform by itself
    import_started: 'system',
    import_completed: 'system',
    import_failed: 'system',
    tickets_closed: 'system',
    tickets_graced: 'system',
    tickets_rejudged: 'system',
    email_sent: 'system',
};
export function actionTypeOf(action) {
    return AUDIT_ACTION_TYPE[action];
}
const ACTIONS_OF_TYPE = (() => {
    const out = Object.fromEntries(AUDIT_ACTION_TYPES.map((t) => [t, []]));
    for (const a of AUDIT_ACTIONS)
        out[AUDIT_ACTION_TYPE[a]].push(a);
    return out;
})();
/** The verbs one of the six words stands for — what the read expands a word into. */
export function actionsOfType(type) {
    return ACTIONS_OF_TYPE[type];
}
